import React, { Component } from 'react';
import Recorder from 'utils/mic-recorder-mod';
import styles from './AudioRecorderAndPlay.module.scss';
import PropTypes from 'prop-types';
import { RecordingAnimation } from './RecordingAnimation';
import { PlayingBackAnimation } from './PlayingBackAnimation';
import { delay } from 'utils/play-multiple-sounds';
import { newSound } from 'utils/audioManager';
import setInitialSize from 'utils/zoomUtil';

import MPEGMode from 'lamejs/src/js/MPEGMode';
import Lame from 'lamejs/src/js/Lame';
import BitStream from 'lamejs/src/js/BitStream';

window.MPEGMode = MPEGMode;
window.Lame = Lame;
window.BitStream = BitStream;

const PULSE_TIMEOUT = 5000;
const AUDIO_TIMEOUT = 10000;
const REASON = {
  didNotRecord: 'Did not record voice'
};

export class AudioRecorder extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isPulsing: false,
      isRecording: false,
      isPlayingBack: false,
      isDisable: false
    };

    this.didUnmount = false;
    this.recordingLength = props.maxRecordingLength;
    this.timers = [];
    this.promise = null;
    this.recording = null;
    this.sound = Promise.resolve();
    this.recorder = null;
  }

  componentDidMount() {
    this.props.inactivityManager.watch(
      PULSE_TIMEOUT,
      () => {
        this.setState({ isPulsing: true });
        this.props.inactivityManager.watch(
          AUDIO_TIMEOUT,
          () => this.props.playPressTheRedButton.play(),
          REASON.didNotRecord
        );
      },
      REASON.didNotRecord,
      true
    );
    if (this.imgRef) {
      this.imgInitialize = setInitialSize(this.imgRef, ['height']);
      this.imgInitialize();
    }
  }

  componentWillUnmount() {
    const { stopAllSounds } = this.props;

    this.didUnmount = true;
    this.promise && this.promise.cancel();
    if (this.sound && 'off' in this.sound) {
      this.sound.off().unload();
    }
    this.props.inactivityManager.clear();
    this.timers.forEach(id => clearTimeout(id));
    this.recorder && this.recorder.stop();
    stopAllSounds && stopAllSounds();
    this.imgInitialize && this.imgInitialize()();
  }

  playAudioCue = type => {
    const { commonSounds } = this.props;

    if (type === 'start') {
      commonSounds.clickStart && commonSounds.clickStart.play();
    } else if (type === 'mid') {
      commonSounds.beep && commonSounds.beep.play();
    } else if (type === 'end') {
      commonSounds.clickEnd && commonSounds.clickEnd.play();
    }
  };

  handlePlayback = async evt => {
    const {
      target: { result }
    } = evt;

    const {
      props: { recordingEnd, stopAllSounds, addRecording },
      recording
    } = this;

    this.promise = newSound(result);
    this.sound = await this.promise;

    if (this.sound && 'once' in this.sound) {
      this.promise = recordingEnd();
      this.promise.then(() => {
        if (this.didUnmount && stopAllSounds) {
          this.promise && this.promise.cancel();
          if (this.sound && 'off' in this.sound) {
            this.sound.off().unload();
          }
          stopAllSounds();
          return;
        }

        this.setState({ isPlayingBack: true, isDisable: false });
        this.sound.once('end', () => addRecording(recording));
        this.sound.once('end', this.promise && this.promise.cancel());
        this.sound.play();
        this.promise = delay(this.recordingLength + 1000).then(() => addRecording(recording));
        // If sound 'end' event has not triggered to cancel this promise we are ending the round
      });
    } else {
      this.rejectRecording(this.recording);
    }
  };

  readFile = (buffer, blob) => {
    this.recording = new File(buffer, `recording_${Date.now()}.mp3`, {
      type: blob.type,
      lastModified: Date.now()
    });

    if (!this.recording || this.recording.length === 0) this.rejectRecording();

    const reader = new FileReader();
    reader.addEventListener('load', evt => {
      this.handlePlayback(evt);
    });
    reader.readAsDataURL(this.recording);
  };

  rejectRecording = (error = false) => {
    console.log('reject recording called'); // eslint-disable-line no-console
    error && console.log(error); // eslint-disable-line no-console
    this.props.addRecording(this.recording || false);
  };

  recordingHandler = async () => {
    const {
      recordingLength,
      props: { inactivityManager, playPressTheRedButton }
    } = this;
    if (this.state.isDisable) return;
    if ('playing' in playPressTheRedButton && playPressTheRedButton.playing())
      playPressTheRedButton.stop();
    inactivityManager.clear();
    this.setState({ isDisable: true });
    this.recorder = new Recorder();
    this.playAudioCue('start');
    // add a delay before actually starting the recording
    await new Promise(resolve => setTimeout(() => resolve(), 500));
    await this.recorder.setup().catch(() => this.rejectRecording());
    this.setState({ isRecording: true });
    // this.promise = await delay(recordingLength); // Record for ${recordingLength} time
    this.timers.push(
      setTimeout(async () => {
        this.playAudioCue('end');
        const [buffer, blob] = await this.recorder
          .stop()
          .getMp3()
          .catch(() => this.rejectRecording());
        this.recorder = null;
        this.setState({ isRecording: false });
        this.readFile(buffer, blob);
      }, recordingLength)
    );
    // add timeouts for every intermediate beep
    if (Math.floor(recordingLength / 1000) > 1) {
      for (let i = 1; i < Math.floor(recordingLength / 1000); i++) {
        this.timers.push(setTimeout(() => this.playAudioCue('mid'), i * 1000));
      }
    }
  };

  render() {
    const {
      props: { roundID },
      state: { isPulsing, isRecording, isPlayingBack },
      recordingLength,
      recordingHandler
    } = this;

    return (
      <div
        className={styles.container}
        id={`recorder-${roundID}`}
        ref={node => (this.imgRef = node)}
      >
        {isPlayingBack ? (
          <div className={styles.buttonSet}>
            <div id="recording" className={styles.recording}>
              <PlayingBackAnimation maxRecordingLength={recordingLength} />
            </div>
          </div>
        ) : (
          <RecordingAnimation
            isActive={isRecording}
            isPulsing={isPulsing}
            clearPulse={() => this.setState({ isPulsing: false })}
            onClick={recordingHandler}
          />
        )}
      </div>
    );
  }
}

AudioRecorder.defaultProps = {
  maxRecordingLength: 5000,
  repeat: 1
};

AudioRecorder.propTypes = {
  recordings: PropTypes.array,
  currentRound: PropTypes.object,
  stopAllSounds: PropTypes.func,
  showRecordButton: PropTypes.bool,
  inactivityManager: PropTypes.object,
  playPressTheRedButton: PropTypes.object,
  maxRecordingLength: PropTypes.number, // in miliseconds
  repeat: PropTypes.number, // how many times should it record?
  onRoundEnd: PropTypes.func, // immediately called after playing back the recording
  addRecording: PropTypes.func.isRequired,
  recordingEnd: PropTypes.func.isRequired,
  studentId: PropTypes.number, // used to build filename,
  logError: PropTypes.func,
  roundID: PropTypes.number,
  commonSounds: PropTypes.object
};

export default AudioRecorder;
